import { auteurPourRequete } from './portees.js';
import { MESSAGE_TYPE_CONVERSATION } from '@taki/data-model';
import { observerPastille, compteServeur, type Pastille } from './pastilles.js';
import {
  type Firestore,
  Timestamp,
  collection,
  doc,
  getCountFromServer,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  runTransaction,
  where,
} from 'firebase/firestore';

/**
 * LA LECTURE DES CONVERSATIONS ET LES NON LUS : UN mecanisme, pour Canaux,
 * le centre de communication et le chatter (contrat conversations du
 * 05/10/2026, section 2c).
 *
 * Un document par personne et par org, `orgs/{orgId}/lectures/{uid}` :
 * `{ orgId, uid, lu: { [conversationId]: Timestamp }, updatedAt }`, lu et
 * ecrit par son uid seul (rules). Remplace `canaux-lecture` (un `contents`
 * par personne), que le centre ne pouvait pas lire sans la porte de l'app
 * Canaux.
 *
 * Les PASTILLES se COMPTENT au serveur (`count()`), par conversation : les
 * messages dont `createdAt` depasse la date de lecture. Un message qu'on
 * envoie ne lit pas les autres messages. Un signal borne recompte les arrivees.
 */

export const LECTURES_COLLECTION = 'lectures';
// Date reste le contrat public ; sa precision Firestore accompagne la lecture SDK.
const precisions = new WeakMap<Date, Timestamp>();
export function horodatageLecture(date: Date): Timestamp { return precisions.get(date) ?? Timestamp.fromDate(date); }
export function cleCurseurLecture(date: Date | undefined): string { const t = date && horodatageLecture(date); return t ? `${t.seconds}:${t.nanoseconds}` : '0'; }

/** Le nombre de conversations qu'un document de lecture peut suivre (recopie dans les rules). */
export const LECTURES_MAX = 500;

export interface LectureDeConversations {
  /** Les dates de lecture de la personne connectee, par conversation. Rend la fonction qui se desabonne. */
  observer(onChange: (lu: Record<string, Date>) => void, onError?: (error: unknown) => void): () => void;
  /** Marque la conversation lue jusqu'a `jusquA` (defaut : maintenant). */
  marquerLu(conversationId: string, jusquA?: Date): Promise<void>;
  /** Le nombre de messages de la conversation ecrits apres `lu` (tous, si `lu` est absent). Compte serveur. */
  observerPastilles(conversationId: string, lu: Date | undefined, recevoir: (p: Pastille) => void, erreur?: (e: unknown) => void, depuis?: number): () => void;
  nonLus(conversationId: string, lu: Date | undefined): Promise<number>;
  /** Acquitte uniquement cet element affiche, avec son horodatage serveur exact. */
  marquerElementAffiche(ref: string, id: string): Promise<void>;
}

/** Le chemin du document de lecture d'une personne. */
export function cheminLecture(orgId: string, uid: string): string {
  return `orgs/${orgId}/${LECTURES_COLLECTION}/${uid}`;
}

/** Pure : le `lu` brut d'un document, en dates. */
export function lecturesDe(brut: unknown): Record<string, Date> {
  const lu = (brut as { lu?: unknown } | null)?.lu;
  const dates: Record<string, Date> = {};
  if (typeof lu !== 'object' || lu === null) return dates;
  for (const [id, valeur] of Object.entries(lu as Record<string, unknown>)) {
    if (valeur instanceof Timestamp) { const date = valeur.toDate(); precisions.set(date, valeur); dates[id] = date; }
    else if (valeur instanceof Date) dates[id] = valeur;
    else if (typeof valeur === 'object' && valeur !== null && 'toDate' in valeur && typeof (valeur as { toDate: unknown }).toDate === 'function') {
      dates[id] = (valeur as { toDate: () => Date }).toDate();
    }
  }
  return dates;
}

export function createLectureDeConversations(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): LectureDeConversations {
  const ref = () => doc(db, 'orgs', orgId, LECTURES_COLLECTION, getActorId());
  return {
    observer(onChange, onError) {
      return onSnapshot(ref(), (snap) => onChange(lecturesDe(snap.exists() ? snap.data() : null)), onError);
    },
    async marquerLu(conversationId, jusquA = new Date()) {
      if (typeof conversationId !== 'string' || conversationId.length === 0) {
        throw new Error('lecture.marquerLu : conversationId est requis.');
      }
      await runTransaction(db, async t => {
        const r = ref(), s = await t.get(r);
        const actuel = lecturesDe(s.data())[conversationId];
        const at = horodatageLecture(jusquA), avant = actuel && horodatageLecture(actuel);
        if (avant && (avant.seconds > at.seconds || avant.seconds === at.seconds && avant.nanoseconds >= at.nanoseconds)) return;
        t.set(r, { orgId, uid: getActorId(), lu: { [conversationId]: at }, updatedAt: serverTimestamp() }, { merge: true });
      });
    },
    async marquerElementAffiche(reference, id) {
      if (!/^[A-Za-z0-9_-]{1,128}$/.test(id)) throw new Error('Element affiche invalide.');
      const parts = reference.split(':');
      const conversation = parts[0] === 'conversation' && parts.length === 2 && /^[A-Za-z0-9_-]{1,128}$/.test(parts[1]!);
      const lia = parts[0] === 'lia' && parts.length === 3 && parts.slice(1).every(p => /^[A-Za-z0-9_-]{1,128}$/.test(p));
      if (!conversation && !lia) throw new Error('Conversation invalide.');
      const cle = conversation ? parts[1]! : reference;
      const element = conversation ? doc(db, 'messages', id) : doc(db, 'orgs', orgId, 'liaConversations', parts[1]!, 'sujets', parts[2]!, 'reponses', id);
      await runTransaction(db, async t => {
        const [m, lecture] = await Promise.all([t.get(element), t.get(ref())]);
        // V3-1 : un element absent (envoi pas encore ecrit, reponse supprimee) n'est pas une erreur : rien a acquitter.
        if (!m.exists()) return;
        const at = m.get('createdAt');
        if (m.get('orgId') !== orgId || conversation && m.get('conversationId') !== parts[1] || !(at instanceof Timestamp)) throw new Error('Element affiche introuvable.');
        const avant = lecture.get('lu')?.[cle] as Timestamp | undefined;
        if (avant && (avant.seconds > at.seconds || avant.seconds === at.seconds && avant.nanoseconds >= at.nanoseconds)) return;
        t.set(ref(), { orgId, uid: getActorId(), lu: { [cle]: at }, updatedAt: serverTimestamp() }, { merge: true });
      });
    },
    observerPastilles(conversationId, lu, recevoir, erreur, depuis) {
      let arret: (() => void) | undefined;
      let stop = false;
      void auteurPourRequete(db, orgId, 'messages', MESSAGE_TYPE_CONVERSATION).then(auteur => {
        if (stop) return;
        const q = query(collection(db, 'messages'), where('type', '==', MESSAGE_TYPE_CONVERSATION), ...(auteur ? [where('createdBy', '==', auteur)] : []), where('orgId', '==', orgId), where('conversationId', '==', conversationId), where('archivedAt', '==', null), where('deletedAt', '==', null));
        arret = observerPastille(q, () => this.nonLus(conversationId, lu), recevoir, erreur, depuis);
      }).catch(cause => erreur?.(cause));
      return () => { stop = true; arret?.(); };
    },
    async nonLus(conversationId, lu) {
      if (!conversationId) throw new Error('Une conversation est requise.');
      const auteur = await auteurPourRequete(db, orgId, 'messages', MESSAGE_TYPE_CONVERSATION);
      const q = query(collection(db, 'messages'), where('type', '==', MESSAGE_TYPE_CONVERSATION), ...(auteur ? [where('createdBy', '==', auteur)] : []), where('orgId', '==', orgId), where('conversationId', '==', conversationId), where('archivedAt', '==', null), where('deletedAt', '==', null), ...(lu ? [where('createdAt', '>', horodatageLecture(lu))] : []));
      const [tous, propres] = await Promise.all([compteServeur(q), compteServeur(auteur ? q : query(q, where('createdBy', '==', getActorId())))]);
      return Math.max(0, tous - propres);
    },
  };
}
